/**
 * Follow-Up Entry Page — Email-only login
 *
 * Redesigned to match the May 2026 mockup:
 * - Single email field (no name, phone, postal code)
 * - Welcome copy
 * - Tenant-branded "Continue" button
 * - "New patient? Start your intake" link
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFollowUp } from '../context/FollowUpContext';
import { useTenant } from '../context/TenantContext';
import { environment } from '../env/environment';
import defaultLogo from '../assets/logo.png';
import activityLogger from '../utils/activityLogger';
import '../styles/formPage.css';
import '../styles/followup.css';

// Truncated base64 PNGs (e.g. a QR code cut off mid-transfer from the backend)
// can decode partially in the browser without ever firing the <img> onError
// event, so a broken/truncated data URI needs to be caught explicitly here.
const isValidPngDataUrl = (url) => {
  if (typeof url !== 'string' || !url.startsWith('data:image/png;base64,')) return true;
  const base64 = url.slice(url.indexOf(',') + 1);
  if (base64.length % 4 !== 0) return false;
  try {
    const binary = atob(base64);
    return binary.slice(-12).includes('IEND');
  } catch {
    return false;
  }
};

const LOOKUP_STEP = [1, 'Email Lookup'];

const FollowUpEntry = () => {
  const navigate = useNavigate();
  const { tenant, loading: tenantLoading } = useTenant();
  const {
    checkEmail,
    isLoading,
    error,
    setError,
    updateFields,
    setIsNewUser,
    setIsExistingUser,
    setFieldsMap: setContextFieldsMap,
  } = useFollowUp();

  const logoToDisplay = tenant?.logoUrl || defaultLogo;
  const brandName = tenant?.brandName || tenant?.name || 'Empower Care';

  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState('');
  const [showNewUserModal, setShowNewUserModal] = useState(false);
  const [qrCodeFailed, setQrCodeFailed] = useState(false);

  // Track follow-up page opened (sent → opened)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('id');
    const token = params.get('token');
    if (id && token) {
      fetch(`${environment.baseUrl}/api/followup/track-open`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, token }),
      }).catch(() => {});
    }
  }, []);

  // The QR stored in tenant branding is the source of truth. When it's
  // missing or corrupt, fall back to a static per-tenant image served from
  // public/qr/<slug>.png. A tenant without that file just 404s into the
  // existing placeholder, and a tenant whose stored QR is fine never reaches
  // the fallback at all — so this only shows up where the stored copy is
  // already broken.
  const storedQr = tenant?.accountBranding?.followup?.qrCodeUrl;
  const storedQrUsable = Boolean(storedQr) && isValidPngDataUrl(storedQr);
  const qrSrc = storedQrUsable
    ? storedQr
    : (tenant?.slug ? `/qr/${tenant.slug}.png` : null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');

    const trimmedEmail = email.trim().toLowerCase();

    if (!trimmedEmail) {
      setLocalError('Email is required');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      setLocalError('Please enter a valid email address');
      return;
    }

    setSubmitting(true);

    try {
      // Save email to context
      updateFields({ email: trimmedEmail });

      // Send webhook
      const tenantId = tenant?.id || window.__TENANT_ID__ || localStorage.getItem('followup_tenant_id');

      activityLogger.clearContext();
      activityLogger.setContext({ email: trimmedEmail, tenantId });
      activityLogger.formStarted();
      activityLogger.stepStarted(...LOOKUP_STEP);
      try {
        await fetch(`${environment.baseUrl}/api/followup/send-step1-webhook`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(tenantId ? { 'X-Tenant-ID': String(tenantId) } : {}),
          },
          body: JSON.stringify({ email: trimmedEmail }),
        });
      } catch (webhookErr) {
        // Non-critical
      }

      // Check if user exists — BLOCK non-existing users
      const result = await checkEmail(trimmedEmail);

      // Locked user → redirect to /verify-otp which shows LockoutScreen with timer
      if (result.isLocked) {
        activityLogger.stepSaveError(...LOOKUP_STEP, {
          message: result.status === 'permanent_lock'
            ? 'Account permanently locked'
            : (result.message || 'Account temporarily locked'),
          status: result.status,
        });
        navigate('/verify-otp');
        return;
      }

      if (result.isExisting) {
        activityLogger.stepCompleted(...LOOKUP_STEP);
        setIsExistingUser(true);
        setIsNewUser(false);
        navigate('/verify-otp');
      } else {
        // Non-existing user — show popup to redirect to intake
        activityLogger.stepSaveError(...LOOKUP_STEP, { message: 'No intake on file for this email' });
        setIsNewUser(false);
        setIsExistingUser(false);
        setShowNewUserModal(true);
        return;
      }
    } catch (err) {
      if (err instanceof TypeError) activityLogger.networkError(...LOOKUP_STEP, err);
      else activityLogger.stepSaveError(...LOOKUP_STEP, err);
      setLocalError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (tenantLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="followup-spinner"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Tenant Logo */}
      <div style={{ textAlign: 'center', marginBottom: '20px', marginTop: '10px' }}>
        <img
          src={logoToDisplay}
          alt={brandName}
          style={{ width: '160px', height: 'auto', maxHeight: '80px', objectFit: 'contain' }}
        />
      </div>

      {/* Card */}
      <div style={{
        maxWidth: '550px',
        margin: '0 auto',
        background: '#ffffff',
        borderRadius: '14px',
        padding: '24px 20px',
        border: '0.5px solid #e2e6ec',
      }}>
        {/* Title */}
        <h3 style={{
          fontSize: '20px',
          fontWeight: '500',
          color: '#1f5b8a',
          margin: '0 0 12px',
          textAlign: 'center',
        }}>
          Established Follow-up Form
        </h3>

        {/* QR Code from tenant branding or placeholder */}
        <div style={{ textAlign: 'center', marginBottom: '14px' }}>
          {qrSrc && !qrCodeFailed ? (
            <>
              <img
                src={qrSrc}
                alt={`QR code for ${brandName || 'follow-up form'}. Scan with your phone or show to your provider.`}
                style={{ width: '160px', height: 'auto', objectFit: 'contain' }}
                onError={() => setQrCodeFailed(true)}
              />

            </>
          ) : (
            <div style={{
              width: '180px',
              height: '180px',
              margin: '0 auto',
              border: '2px dashed #c2cad3',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: '8px',
              backgroundColor: '#fafbfc',
            }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#8a96a3" strokeWidth="1.5">
                <rect x="3" y="3" width="7" height="7" rx="1"/>
                <rect x="14" y="3" width="7" height="7" rx="1"/>
                <rect x="3" y="14" width="7" height="7" rx="1"/>
                <rect x="14" y="14" width="7" height="7" rx="1"/>
              </svg>
              <span style={{ fontSize: '11px', color: '#8a96a3', textAlign: 'center', lineHeight: '1.3', padding: '0 10px' }}>
                QR code not configured.<br/>Contact your administrator.
              </span>
            </div>
          )}
        </div>

        {/* Welcome copy */}
        <p style={{
          fontSize: '14px',
          color: '#5b6773',
          margin: '0 0 18px',
          textAlign: 'center',
          lineHeight: '1.5',
        }}>
          Welcome back! Your continued progress matters to us, please complete the secure form below prior to your visit.
        </p>

        {/* Email field */}
        <form onSubmit={handleSubmit} noValidate autoComplete="off">
          <label style={{
            display: 'block',
            fontSize: '14px',
            color: '#5b6773',
            margin: '0 0 5px',
            fontWeight: '500',
          }}>
            Email
          </label>
          <input
            type="email"
            id="followup-email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setLocalError(''); }}
            placeholder="you@example.com"
            disabled={submitting}
            required
            autoComplete="off"
            aria-invalid={!!(localError || error)}
            aria-describedby={(localError || error) ? 'email-error' : undefined}
            style={{
              background: '#fafbfc',
              border: (localError || error) ? '1.5px solid #c33' : '0.5px solid #c2cad3',
              color: '#1a1a1f',
              padding: '10px 12px',
              borderRadius: '8px',
              width: '100%',
              fontSize: '15px',
              fontFamily: 'inherit',
              boxSizing: 'border-box',
              outline: 'none',
            }}
          />

          {/* Error */}
          {(localError || error) && (
            <div id="email-error" role="alert" style={{
              color: '#c33',
              fontSize: '12px',
              marginTop: '8px',
              textAlign: 'center',
            }}>
              {localError || error}
            </div>
          )}

          {/* Continue button */}
          <div style={{ marginTop: '12px' }}>
            <button
              type="submit"
              disabled={submitting || isLoading}
              style={{
                background: tenant?.primaryColor || '#4a7a2c',
                color: '#fff',
                border: 'none',
                padding: '11px',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: '500',
                cursor: submitting ? 'not-allowed' : 'pointer',
                width: '100%',
                fontFamily: 'inherit',
                opacity: submitting ? 0.7 : 1,
              }}
            >
              {submitting ? 'Please wait...' : 'Continue'}
            </button>
          </div>
        </form>

        {/* New patient link */}
        <div style={{ textAlign: 'center', marginTop: '16px' }}>
          <a
            href={tenant?.clientDomain
              ? `https://${tenant.clientDomain}`
              : window.location.hostname === 'localhost'
                ? `http://localhost:3000${tenant?.slug ? `?tenant=${tenant.slug}` : ''}`
                : '/'
            }
            style={{
              color: '#2486c5',
              fontSize: '14px',
              textDecoration: 'none',
            }}
          >
            New patient? Start your intake
          </a>
        </div>
      </div>

      {/* New User Modal — redirect to intake */}
      {showNewUserModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10000, padding: '20px',
        }} onClick={() => setShowNewUserModal(false)}>
          <div onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" style={{
            background: '#fff', borderRadius: '16px', maxWidth: '440px', width: '100%', padding: '32px 28px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)', position: 'relative',
          }}>
            {/* Close */}
            <button onClick={() => setShowNewUserModal(false)} style={{
              position: 'absolute', top: '12px', right: '12px', background: 'none', border: 'none',
              fontSize: '22px', color: '#999', cursor: 'pointer', padding: '4px 8px', lineHeight: 1,
            }}>×</button>

            {/* Icon */}
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              <div style={{
                width: '56px', height: '56px', borderRadius: '50%', background: '#e3f2fd',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#1976d2" strokeWidth="2">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="8.5" cy="7" r="4"/>
                  <line x1="20" y1="8" x2="20" y2="14"/>
                  <line x1="23" y1="11" x2="17" y2="11"/>
                </svg>
              </div>
            </div>

            <h2 style={{ textAlign: 'center', fontSize: '22px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 8px' }}>
              Let's Update Your Paperwork
            </h2>
            <p style={{ textAlign: 'center', color: '#666', fontSize: '14px', lineHeight: '1.6', margin: '0 0 24px' }}>
              We couldn't find this email in our new system. Please complete the intake form to update your paperwork, even if you've visited us before.
            </p>

            <button
              onClick={() => {
                setShowNewUserModal(false);
                const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
                const slug = tenant?.slug || new URLSearchParams(window.location.search).get('tenant') || '';
                if (isLocal) {
                  window.location.href = `http://localhost:3000/?tenant=${slug}`;
                } else if (tenant?.clientDomain || tenant?.client_domain) {
                  window.location.href = `https://${tenant.clientDomain || tenant.client_domain}`;
                } else {
                  window.location.href = `https://${window.location.hostname.replace('followup.', 'intake.').replace('followup.', 'newintake.')}`;
                }
              }}
              style={{
                width: '100%', padding: '13px 16px', fontSize: '14px', fontWeight: '600', border: 'none',
                borderRadius: '10px', cursor: 'pointer', color: '#fff',
                background: 'var(--color-primary, #1a7fc1)',
              }}
            >
              Update My Paperwork
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FollowUpEntry;
